import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Team } from './models';
import { baseApi } from '@centerpiece/components';

export const team = baseApi
  .enhanceEndpoints({ addTagTypes: ['Teams', 'Team'] })
  .injectEndpoints({
    endpoints: builder => ({
      getTeams: builder.query<Team[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'team',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'Team' as const, id })),
                'Teams',
              ]
            : ['Teams'],
      }),
      getTeam: builder.query<Team, string>({
        query: id => ({
          url: `team/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [{ type: 'Team', id: arg }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createTeam: builder.mutation({
        query: request => ({
          url: 'team',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Teams'],
      }),
      updateTeam: builder.mutation<Team, Team>({
        query: request => ({
          url: `team/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [{ type: 'Team', id: arg.id }],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            team.util.updateQueryData('getTeam', `${id}`, draft => {
              Object.assign(draft, patch);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(team.util.invalidateTags(['Teams']))
             */
            dispatch(team.util.invalidateTags(['Teams']));
          }
        },
      }),
      deleteTeam: builder.mutation<Team, number>({
        query: id => ({
          url: `team/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result ? [{ type: 'Team' as const, arg }, 'Teams'] : ['Teams'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetTeamsQuery,
  useGetTeamQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  usePrefetch: useTeamsPrefetch,
} = team;
const selectTeamsRoot = team.endpoints.getTeams.select({});
export const selectTeams = createSelector(
  selectTeamsRoot,
  team => team.data || []
);
